import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import { OnboardingNote } from "../components/site/Highlights";
import { LateFeeNote } from "@/components/site/LateFeeNote";
import { FeesByTerm } from "@/components/site/FeesByTerm";
import { sitePricingQuery } from "@/lib/pricing-query";
import { currentSchoolYearLabel } from "@/lib/school-year";
import { safeLoader } from "@/lib/safe-loader";

const title = `Avgifter läsåret ${currentSchoolYearLabel()} | SIS Estepona`;
const description =
  "Terminsavgifter för förskola, grundskola, distansstudier, fritids och Malmö FF:s fotbollsakademi vid Swedish International School i Estepona.";

export const Route = createFileRoute("/avgifter")({
  loader: ({ context }) => safeLoader("sv.fees", () => context.queryClient.ensureQueryData(sitePricingQuery), { pricing: null, stale: false, fetchedAt: null, error: "unavailable", termSettings: null }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/avgifter") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/avgifter"),
  }),
  component: Avgifter,
});


function Avgifter() {
  const pricingData = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow="Avgifter"
        title="Avgifter och betalningsplaner"
        intro="Avgiften betalas per termin i förskott. Avbetalning kan erbjudas efter överenskommelse och anges då i avtalet. Vi har också familjerabatter för större familjer. Hör gärna av dig om du har frågor om avgifterna."
      />

      <section className="mx-auto max-w-4xl px-5 pt-8">
        <p className="rounded-full border border-primary/15 bg-primary/5 px-5 py-3 text-sm text-foreground/75 shadow-soft">
          Priserna gäller för HT26, VT27, HT27 och VT28 och publicerades 28 september 2026. Nya priser för en termin publiceras här senast 10 januari (hösttermin) och 1 augusti (vårtermin).
        </p>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-8 sm:py-10">
        <div className="rounded-lg border border-border bg-card p-5 shadow-soft sm:p-8">
          <h2 className="text-2xl font-semibold">Inskrivning och rabatter</h2>
          <p className="mt-2 text-sm text-muted-foreground italic">Inskrivningsavgiften kallas ibland registreringsavgift – det är samma avgift.</p>
          <div className="mt-6 rounded-md border border-border bg-muted/40 p-5 text-sm leading-relaxed text-foreground/85">
            <p>Inskrivningsavgiften betalas en gång per elev och står per termin i listorna nedan.</p>
            <p className="mt-3">Korttidsregistrering har en lägre inskrivningsavgift, giltig för två terminer inom samma skolår.</p>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="rounded-md border border-primary/15 bg-primary/5 p-5"><strong className="text-2xl text-primary">20 %</strong><p className="mt-1 text-sm text-foreground/75">Familjerabatt för det tredje barnet, på den lägsta avgiften.</p></div>
            <div className="rounded-md border border-primary/15 bg-primary/5 p-5"><strong className="text-2xl text-primary">50 %</strong><p className="mt-1 text-sm text-foreground/75">Familjerabatt för det fjärde barnet, på den lägsta avgiften.</p></div>
          </div>
        </div>

        <h2 className="mt-14 text-2xl font-semibold">Avgifter per termin</h2>

        <FeesByTerm locale="sv" initialData={pricingData} />


        <div className="mt-10 space-y-2 text-sm leading-relaxed text-muted-foreground">
          <LateFeeNote locale="sv" />
          <p>
            Korttid kan inte kombineras med veckor och månader. Maximalt två veckors korttid kan
            bokas per termin, annars bokas månad eller termin. Maximalt två månader kan bokas per
            termin, annars bokas hel termin.
          </p>
          <p>
            Vid deltagande i Malmö FF:s fotbollsakademi hjälper skolan till med hela anmälningsprocessen och de villkor som gäller för deltagandet. Malmö FF:s fotbollsakademi kräver skolgång med Sofia Distans och skolplats hos Swedish
            International School.
          </p>
        </div>

        <OnboardingNote className="mt-12 bg-card" />

        <div className="mt-8 rounded-xl border border-border bg-sand p-6 sm:p-7">
          <h3 className="text-lg font-semibold">Unikt förmånskort</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Swedish International School samarbetar med Villa Padierna. När samarbetet möjliggör
            det kan våra familjer få tillgång till ett förmånskort som ger rabatter och förmåner i
            området.
          </p>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Det kan till exempel röra sig om:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
            <li>Padel och tennis</li>
            <li>Golf</li>
            <li>Villa Padiernas restauranger</li>
          </ul>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            Förmånerna förmedlas inom ramen för samarbetet och kan ändras över tid. Vi kan inte
            garantera innehåll eller tillgång i förväg.
          </p>
        </div>
      </section>

      <Prose>
        <h2>Vad avgiften omfattar</h2>
        <p>
          Terminsavgiften avser undervisning enligt svensk läroplan med svensktalande personal,
          lokaler och skolans ordinarie aktiviteter. Inskrivningsavgiften betalas
          en gång per elev vid antagning. Tillval som fritids, fotbollsakademi och padel- eller
          tennisprofil ligger utanför terminsavgiften och framgår i listorna ovan.
        </p>

        <h2>Betalning och terminer</h2>
        <p>
          Skolavgiften ska vara betald innan eleven börjar på skolan. Vårterminens avgift betalas senast 25 oktober och höstterminens senast 1 maj. Börjar eleven under en pågående termin betalas hela terminsavgiften inom tre arbetsdagar från fakturan och före första skoldagen. Terminernas start- och slutdatum hittar ni bland{" "}
          <a className="text-primary link-quiet" href="/lasarstider">läsårstiderna</a>.
        </p>

        <h2>Familjerabatt och korttid</h2>
        <p>
          Familjer med flera barn i skolan får rabatt enligt listan ovan, beräknad på den lägsta
          avgiften. För familjer som bara är i Spanien en del av läsåret finns korttidsregistrering,
          med lägre inskrivningsavgift och avgift utifrån vistelsens längd. I{" "}
          <a className="text-primary link-quiet" href="/ansokan">ansökan</a> räknas summan fram
          direkt när ni fyller i skolform, period och tillval.
        </p>

        <h2>Statsbidrag</h2>
        <p>
          Familjer där en vårdnadshavare är utsänd i arbete kan i vissa fall ansöka om statsbidrag hos
          Skolverket för distansstudier på högstadie- och gymnasienivå. Villkoren avgörs av Skolverket
          – läs mer på sidan om{" "}
          <a className="text-primary link-quiet" href="/svenska-skolverket-och-kompletterande-svensk-undervisning">Skolverket och kompletterande svensk undervisning</a>.
        </p>
      </Prose>
      <FaqBlock items={faqFor("avgifter", "sv")} locale="sv" />


      <CtaBand
        title="Frågor om avgifter?"
        text="Kontakta oss så går vi igenom avgifterna för er familj."
      />
      <div className="h-20" />
    </>
  );
}
